import { useTranslation } from 'react-i18next';
import { formatTimezoneLabel } from '@/lib/format/timezone';
import { Clock3, Edit3, Trash2, Users } from 'lucide-react';
import type { PublishQueueFieldsFragment } from '@/generated/graphql';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { parseWeeklySlots } from '@/lib/publishQueues/weeklySlots';
import { isPublishPlatform } from '@/lib/publishing/types';
import type { QueueAccount } from './QueueAccountsFieldset';
import { QueueAccountSummary } from './QueueAccountSummary';
import { QueueMiniCalendar } from './QueueMiniCalendar';
import { QueueUpcomingPosts } from './QueueUpcomingPosts';
import type { QueueScheduledPost } from '@/lib/publishQueues/selectQueueScheduledPosts';

type Props = {
  queue: PublishQueueFieldsFragment;
  connections: QueueAccount[];
  upcoming: { posts: QueueScheduledPost[]; loading: boolean; error: boolean };
  onEdit: () => void;
  onDelete: () => void;
};

export function PublishQueueCard({
  queue, connections, upcoming, onEdit, onDelete,
}: Props) {
  const { t } = useTranslation();
  const visibleConnections = connections.filter((connection) => connection.status === 'active' && isPublishPlatform(connection.platform));
  return (
    <Card role="article" aria-label={queue.name} className="flex min-w-0 flex-col">
      <CardHeader className="space-y-3 p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <CardTitle title={queue.name} className="min-w-0 truncate text-xl">{queue.name}</CardTitle>
          <Badge className="shrink-0" variant={queue.active ? 'default' : 'secondary'}>
            {t(queue.active ? 'publishQueues.active' : 'publishQueues.inactive')}
          </Badge>
        </div>
        <div className="flex min-w-0 flex-wrap gap-2">
          <Badge variant="outline">
            {t(queue.multi_account_mode === 'round_robin' ? 'publishQueues.mode.roundRobin' : 'publishQueues.mode.crossPost')}
          </Badge>
          <Badge variant="outline" title={formatTimezoneLabel(queue.timezone)} className="max-w-full truncate">{formatTimezoneLabel(queue.timezone)}</Badge>
          {queue.spacing_scope === 'account' ? (
            <Badge variant="outline">{t('publishQueues.spacingBadge')}</Badge>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="flex min-w-0 flex-1 flex-col gap-5 p-4 pt-0 sm:p-5 sm:pt-0">
        <div className="min-w-0 space-y-2.5">
          <h3 className="flex items-center gap-2 text-sm font-medium">
            <Clock3 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            {t('publishQueues.schedule')}
          </h3>
          <QueueMiniCalendar slots={parseWeeklySlots(queue.weekly_slots)} />
          <p className="text-xs text-muted-foreground">{t('publishQueues.gap', { minutes: queue.min_gap_minutes })}</p>
        </div>
        <QueueUpcomingPosts posts={upcoming.posts} timezone={queue.timezone}
          loading={upcoming.loading} error={upcoming.error} />
        <div className="min-w-0 space-y-3">
          <h3 className="flex items-center gap-2 text-sm font-medium">
            <Users className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            {t('publishQueues.accounts', { count: visibleConnections.length })}
          </h3>
          <QueueAccountSummary connections={visibleConnections} />
        </div>
        <div className="mt-auto flex gap-2 border-t pt-4">
          <Button type="button" variant="outline" size="sm" className="flex-1" onClick={onEdit}>
            <Edit3 className="mr-2 h-4 w-4" />{t('publishQueues.edit')}
          </Button>
          <Button type="button" variant="ghost" size="icon" className="h-9 w-9" aria-label={t('publishQueues.delete')} onClick={onDelete}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
